// ProductsPage.jsx - Place in components/dashboard/ProductsPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getProducts, addProduct, updateProduct, deleteProduct, getBrands } from '../../utils/api';
import { ProductModal } from './ProductModal';

export const ProductsPage = () => {
    const [rowData, setRowData] = useState([]);
    const [brands, setBrands] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const gridRef = useRef();

    useEffect(() => {
        fetchProducts();
        fetchBrands();
    }, []);

    const fetchProducts = async () => {
        try {
            const res = await getProducts({});
            const products = res.data.map(p => ({
                ...p,
                specifications: JSON.parse(p.specifications || '{}'),
                images: JSON.parse(p.images || '[]')
            }));
            setRowData(products);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchBrands = async () => {
        try {
            const res = await getBrands();
            setBrands(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const columnDefs = [
        { field: 'id', headerName: 'ID', filter: true, sortable: true },
        { field: 'title', headerName: 'Title', filter: true, sortable: true, flex: 2 },
        { field: 'description', headerName: 'Description', filter: true, sortable: true, flex: 2, cellRenderer: (params) => params.value?.substring(0, 50) + '...' },
        { field: 'category', headerName: 'Category', filter: 'agTextColumnFilter', sortable: true },
        { field: 'brand_name', headerName: 'Brand', filter: true, sortable: true },
        { field: 'price', headerName: 'Price', filter: 'agNumberColumnFilter', sortable: true, valueFormatter: (params) => `$${params.value}` },
        { field: 'update_date', headerName: 'Updated', filter: 'agDateColumnFilter', sortable: true },
        {
            headerName: 'Actions',
            cellRenderer: (params) => (
                <div className="flex space-x-2">
                    <button onClick={() => handleEdit(params.data)} className="px-2 py-1 bg-blue-500 text-white rounded text-sm">Edit</button>
                    <button onClick={() => handleDelete(params.data.id)} className="px-2 py-1 bg-red-500 text-white rounded text-sm">Delete</button>
                </div>
            ),
            width: 150
        }
    ];

    const defaultColDef = {
        resizable: true,
        sortable: true,
        filter: true
    };

    const onGridReady = (params) => {
        gridRef.current.api = params.api;
    };

    const handleAdd = () => {
        setIsEdit(false);
        setCurrentProduct(null);
        setShowModal(true);
    };

    const handleEdit = (product) => {
        setIsEdit(true);
        setCurrentProduct(product);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure?')) {
            try {
                await deleteProduct(id);
                fetchProducts();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setCurrentProduct(null);
    };

    const handleModalSubmit = async (formData) => {
        try {
            if (isEdit && currentProduct) {
                await updateProduct(formData); // Updated: Pass only formData (ID already appended in ProductModal)
            } else {
                await addProduct(formData);
            }
            fetchProducts();
            handleModalClose();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="bg-white p-6 rounded shadow">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Products</h1>
                <button onClick={handleAdd} className="px-4 py-2 bg-green-500 text-white rounded">Add Product</button>
            </div>
            <div className="ag-theme-alpine" style={{ height: 500, width: '100%' }}>
                <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    animateRows={true}
                />
            </div>
            <ProductModal
                show={showModal}
                onClose={handleModalClose}
                onSubmit={handleModalSubmit}
                isEdit={isEdit}
                currentItem={currentProduct}
                brands={brands}
            />
        </div>
    );
};